import { useEffect, useRef, useState } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { BrandLogo } from '../components/BrandAssets'
import { useAuth } from '../contexts/AuthContext'
import { api, ApiError } from '../lib/api'
import { loadRuntimeConfig } from '../lib/runtime-config'

interface GoogleIdentity {
  accounts: {
    id: {
      initialize(options: {
        client_id: string
        callback(response: { credential: string }): void
        ux_mode: 'popup'
      }): void
      renderButton(element: HTMLElement, options: Record<string, string | number>): void
    }
  }
}

declare global {
  interface Window {
    google?: GoogleIdentity
  }
}

function loadGoogleScript() {
  return new Promise<void>((resolve, reject) => {
    if (window.google?.accounts.id) {
      resolve()
      return
    }
    const existing = document.querySelector<HTMLScriptElement>('script[data-google-identity]')
    if (existing) {
      if (existing.dataset.googleIdentityFailed === 'true') {
        existing.remove()
        void loadGoogleScript().then(resolve, reject)
        return
      }
      existing.addEventListener('load', () => resolve(), { once: true })
      existing.addEventListener('error', () => {
        existing.dataset.googleIdentityFailed = 'true'
        reject(new Error('Google 登入程式載入失敗'))
      }, { once: true })
      return
    }
    const script = document.createElement('script')
    script.src = 'https://accounts.google.com/gsi/client'
    script.async = true
    script.dataset.googleIdentity = 'true'
    script.onload = () => resolve()
    script.onerror = () => {
      script.dataset.googleIdentityFailed = 'true'
      reject(new Error('Google 登入程式載入失敗'))
    }
    document.head.appendChild(script)
  })
}

export function LoginPage() {
  const { user, refresh } = useAuth()
  const [accepted, setAccepted] = useState(false)
  const [status, setStatus] = useState('')
  const [error, setError] = useState('')
  const [prepareFailed, setPrepareFailed] = useState(false)
  const [prepareAttempt, setPrepareAttempt] = useState(0)
  const buttonRef = useRef<HTMLDivElement | null>(null)
  const alertRef = useRef<HTMLDivElement | null>(null)
  const restoreGoogleButtonAfterFailure = useRef(false)
  const location = useLocation()
  const navigate = useNavigate()
  const destination = (location.state as { from?: string } | null)?.from || '/projects'

  useEffect(() => {
    if (!accepted || !buttonRef.current) return
    let active = true
    buttonRef.current.replaceChildren()
    setStatus('正在準備登入…')
    if (!restoreGoogleButtonAfterFailure.current) setError('')
    restoreGoogleButtonAfterFailure.current = false
    setPrepareFailed(false)

    void Promise.all([loadRuntimeConfig(), loadGoogleScript(), api<{ loginCsrf: string }>('/api/auth/csrf')])
      .then(([config, , csrf]) => {
        if (!active || !buttonRef.current || !window.google) return
        buttonRef.current.replaceChildren()
        window.google.accounts.id.initialize({
          client_id: config.googleClientId,
          ux_mode: 'popup',
          callback: ({ credential }) => {
            buttonRef.current?.replaceChildren()
            setStatus('正在建立安全 Session…')
            setError('')
            void api('/api/auth/google', {
              method: 'POST',
              body: JSON.stringify({
                credential,
                loginCsrf: csrf.loginCsrf,
                policyVersion: '2026-07-29',
                ageAndPolicyAccepted: true,
              }),
            })
              .then(async () => {
                await refresh()
                navigate(destination, { replace: true })
              })
              .catch((caught: ApiError) => {
                setError(caught.message)
                setStatus('')
                restoreGoogleButtonAfterFailure.current = true
                setPrepareAttempt((attempt) => attempt + 1)
              })
          },
        })
        window.google.accounts.id.renderButton(buttonRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'signin_with',
          shape: 'pill',
          width: 320,
          locale: 'zh_TW',
        })
        setStatus('')
      })
      .catch(() => {
        if (active) {
          setError('無法載入 Google 登入，請確認網路連線後再試。')
          setStatus('')
          setPrepareFailed(true)
        }
      })
    return () => {
      active = false
    }
  }, [accepted, destination, navigate, prepareAttempt, refresh])

  useEffect(() => {
    if (error) alertRef.current?.focus()
  }, [error])

  if (user) return <Navigate to={destination} replace />

  return (
    <div className="page login-page">
      <section className="login-stage" aria-labelledby="login-stage-title">
        <div className="login-stage__media">
          <div className="login-stage__media-content">
            <BrandLogo />
            <span className="eyebrow">開始建立你的編排</span>
            <h1 id="login-stage-title">保存你的段落作品</h1>
            <p>登入後可以保存編排、公開分享，也能複製其他人的作品。</p>
          </div>
        </div>
        <div className="login-stage__auth">
          <div className="policy-consent">
            <label className="checkbox-row">
              <input type="checkbox" checked={accepted} onChange={(event) => setAccepted(event.target.checked)} />
              <span>
                我已年滿 13 歲，並同意 <Link to="/policies#privacy">隱私權政策</Link>、{' '}
                <Link to="/policies#terms">使用條款</Link>、{' '}
                <Link to="/policies#community">社群規則</Link> 與{' '}
                <a href="https://www.youtube.com/t/terms" target="_blank" rel="noreferrer">YouTube 服務條款</a>。
              </span>
            </label>
            {!accepted && <p className="login-stage__hint">勾選後即可使用 Google 帳號登入。</p>}
            <div ref={buttonRef} className="google-button-slot">
              {!accepted && <button className="button button--large button--full" disabled>使用 Google 帳號登入</button>}
            </div>
            {status && <p className="login-stage__status" role="status" aria-live="polite">{status}</p>}
            {error && (
              <>
                <div ref={alertRef} className="alert alert--danger" role="alert" tabIndex={-1}>{error}</div>
                {prepareFailed && (
                  <button className="button button--full" type="button" onClick={() => setPrepareAttempt((attempt) => attempt + 1)}>
                    重新準備登入
                  </button>
                )}
              </>
            )}
          </div>
          <aside className="security-note">
            <strong>我們不會取得你的 Google 密碼</strong>
            <p>不保存 Google refresh token，也不把登入 Token 放進 localStorage。</p>
            <p>登入後由 iLoveSing 建立固定 7 天、可立即撤銷的本站 Session。</p>
          </aside>
        </div>
      </section>
    </div>
  )
}
